import { useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import {
  UnitTransferRequestStatusEnumType,
  UnitTransferSignatureStatusEnumType,
  UnitTransferSignerRoleEnumType,
  UnitTransferSigningModeEnumType,
  UnitTransferUnitsLocationEnumType,
} from '__generated__/graphql';
import { FC } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import SidebarLayout, {
  SidebarLayoutContent,
  SidebarLayoutNavigation,
} from 'layouts/SidebarLayout';

import DetailsPanel from 'components/DetailsPanel';
import ErrorMessage from 'components/ErrorMessage';
import SectionCard from 'components/SectionCard';

import Alert from 'primitives/Alert';
import Badge from 'primitives/Badge';
import Button from 'primitives/Button';
import LoadingIndicator from 'primitives/LoadingIndicator';

import { getUser } from 'utils/auth';
import unitTransferEscrowExceptionStatusToReadable from 'utils/enums/unit-transfer-escrow-exception-status-to-readable';
import getUnitTransferNextAction from 'utils/enums/unit-transfer-next-action';
import unitTransferReasonToReadable from 'utils/enums/unit-transfer-reason-to-readable';
import unitTransferSlaToBadge from 'utils/enums/unit-transfer-sla-to-badge';
import { getUnitTransferStage } from 'utils/enums/unit-transfer-stage';
import unitTransferStatusToReadable from 'utils/enums/unit-transfer-status-to-readable';

import NextActionCard from './NextActionCard';
import RequestEscrowExceptionButton from './RequestEscrowExceptionButton';
import StageStepper from './StageStepper';
import SubmitUnitTransferRequestButton from './SubmitUnitTransferRequestButton';
import Timeline from './Timeline';
import WithdrawUnitTransferRequestButton from './WithdrawUnitTransferRequestButton';
import getUnitTransferRequestTabs from './tabs';

const UNIT_TRANSFER_REQUEST_DETAIL_QUERY = gql(`
  query UnitTransferRequestDetail($id: ID!) {
    unitTransferRequest(id: $id) {
      id
      status
      reason
      reasonDescription
      relationship
      unitsLocation
      slaDaysRemaining
      jointDematDetected
      jointDematCheckStatus
      permissions {
        canSubmit
        canWithdraw
      }
      transferor {
        id
        name
      }
      transferee {
        id
        name
      }
      transferorEntity {
        id
      }
      transfereeEntity {
        id
      }
      schemes {
        id
        schemeName
        isin
        numberOfUnits
        investmentAmount
      }
      escrowException {
        id
        status
        note
      }
      signatureRequests {
        id
        status
        signingMode
        signers {
          id
          role
        }
      }
      events {
        id
        type
        note
        createdAt
        visibleToInvestor
        actor {
          id
          name
        }
      }
    }
  }
`);

const UnitTransferRequestTracker: FC = () => {
  const { requestId } = useParams<{ requestId: string }>() as { requestId: string };
  const navigate = useNavigate();
  const user = getUser();

  const { data, loading, error, refetch } = useQuery(UNIT_TRANSFER_REQUEST_DETAIL_QUERY, {
    variables: { id: requestId },
  });

  // The transfers list lives on the investment entity page, so go back to the viewer's own entity.
  const backEntityId = data
    ? data.unitTransferRequest.transferor.id === user.id
      ? data.unitTransferRequest.transferorEntity.id
      : data.unitTransferRequest.transfereeEntity?.id
    : null;
  const backPath = backEntityId
    ? `/invest/investment-entities/${backEntityId}/unit-transfers`
    : '/invest/investment-entities';

  const renderContent = () => {
    if (loading && !data) return <LoadingIndicator />;

    if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

    const request = data.unitTransferRequest;
    const { label: statusLabel, color: statusColor } = unitTransferStatusToReadable(request.status);
    const totalUnits = request.schemes.reduce((sum, scheme) => sum + scheme.numberOfUnits, 0);
    const totalAmount = request.schemes.reduce((sum, scheme) => sum + scheme.investmentAmount, 0);
    const visibleEvents = request.events.filter(event => event.visibleToInvestor);

    // Everything the tracker says about "your turn" hangs off which side of the transfer the
    // viewer is on — the same status is an action for one party and a wait for the other.
    const viewerIsTransferor = request.transferor.id === user.id;
    const viewerRole = viewerIsTransferor ? 'TRANSFEROR' : 'TRANSFEREE';
    const viewerSignerRole = viewerIsTransferor
      ? UnitTransferSignerRoleEnumType.Transferor
      : UnitTransferSignerRoleEnumType.Transferee;
    const counterparty = viewerIsTransferor ? request.transferee : request.transferor;

    const stage = getUnitTransferStage(request.status);
    const nextAction = getUnitTransferNextAction({
      status: request.status,
      viewerRole,
      counterpartyName: counterparty.name,
      requestId: request.id,
    });
    const slaBadge = unitTransferSlaToBadge(request.slaDaysRemaining);

    // Mirrors the backend's BUS_076/BUS_077 gate on requestEscrowException, plus the shield rule
    // that restricts it to the transferor. Without this the escrow guidance shown in the wizard
    // ("you'll see this option on the tracker") points at an action that never appears.
    // The acknowledgement screen lives on its own route and was only ever linked from the
    // "please review this transfer" email — which silently no-ops while its SendGrid template is a
    // placeholder. That left the transferee looking at a read-only tracker with no way to accept or
    // decline, and the whole request stuck at PENDING_TRANSFEREE_ACKNOWLEDGEMENT. Mirrors the
    // backend's isUnitTransferTransfereeForAcknowledgement rule, plus a status check so the button
    // only appears while there is actually something to respond to.
    const canAcknowledge =
      !viewerIsTransferor &&
      request.status === UnitTransferRequestStatusEnumType.PendingTransfereeAcknowledgement;

    const canRequestEscrowException =
      viewerIsTransferor &&
      request.unitsLocation === UnitTransferUnitsLocationEnumType.AlTrustEscrow &&
      request.status === UnitTransferRequestStatusEnumType.UnderReview &&
      !request.escrowException;

    // Manual-mode documents ready for the viewer's signature but not yet uploaded — mirrors the
    // gating logic in the Signatures tab (a document only "counts" if the viewer's role is
    // actually among its signers; institutional signers have no investor-app account at all).
    const pendingManualSignatureCount = request.signatureRequests.filter(
      signatureRequest =>
        signatureRequest.signingMode === UnitTransferSigningModeEnumType.Manual &&
        signatureRequest.status === UnitTransferSignatureStatusEnumType.Sent &&
        signatureRequest.signers.some(signer => signer.role === viewerSignerRole)
    ).length;

    return (
      <div className="space-y-6">
        <SectionCard bodyClassName="px-4 py-4 space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge label={statusLabel} color={statusColor} showIndicator={true} />
              {slaBadge ? <Badge label={slaBadge.label} color={slaBadge.color} /> : null}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {canAcknowledge ? (
                <Button
                  onClick={() => navigate(`/invest/unit-transfers/${request.id}/acknowledge`)}
                >
                  Review and respond
                </Button>
              ) : null}
              {canRequestEscrowException ? (
                <RequestEscrowExceptionButton
                  requestId={request.id}
                  onRequested={() => refetch()}
                />
              ) : null}
              {request.permissions.canSubmit ? (
                <SubmitUnitTransferRequestButton
                  requestId={request.id}
                  onSubmitted={() => refetch()}
                />
              ) : null}
              {request.permissions.canWithdraw ? (
                <WithdrawUnitTransferRequestButton
                  requestId={request.id}
                  onWithdrawn={() => refetch()}
                />
              ) : null}
            </div>
          </div>

          <StageStepper stage={stage} />
        </SectionCard>

        {pendingManualSignatureCount > 0 ? (
          <Alert
            type="warning"
            title={
              pendingManualSignatureCount === 1
                ? '1 document is waiting on your signature'
                : `${pendingManualSignatureCount} documents are waiting on your signature`
            }
            message="Download the blank template, sign it outside the system, and upload the signed copy back."
            buttonText="Go to Signatures"
            onClick={() => navigate(`/invest/unit-transfers/${request.id}/signatures`)}
          />
        ) : null}

        {request.jointDematDetected ? (
          <Alert
            type="warning"
            title="Joint demat account detected"
            message="A joint demat account was found on the transferee's CML. Our team is reviewing this before the transfer proceeds."
          />
        ) : null}

        {request.unitsLocation === UnitTransferUnitsLocationEnumType.AlTrustEscrow &&
        request.escrowException ? (
          <Alert
            type="info"
            title={`Escrow-direct exception: ${unitTransferEscrowExceptionStatusToReadable(
              request.escrowException.status
            )}`}
            message={request.escrowException.note ?? undefined}
          />
        ) : null}

        {/* flex, not grid — DetailsPanel hardcodes an md:col-span-2 meant for a grid parent, which
            would make it fill both columns and crowd out the next-action card entirely. Flexbox
            basis controls the actual width instead, so that class is simply inert here. */}
        <div className="flex flex-col gap-4 md:flex-row md:items-start">
          <div className="md:w-1/2">
            <DetailsPanel
              title="Transfer details"
              displayHorizontally
              data={[
                { label: 'From', value: request.transferor.name },
                { label: 'To', value: request.transferee.name },
                { label: 'Reason', value: unitTransferReasonToReadable(request.reason) },
                { label: 'Description', value: request.reasonDescription },
                { label: 'Relationship', value: request.relationship },
                { label: 'Schemes', value: request.schemes.length, type: 'NUMBER' },
                { label: 'Total units', value: totalUnits, type: 'NUMBER' },
                { label: 'Total amount', value: totalAmount, type: 'CURRENCY' },
              ]}
            />
          </div>
          {nextAction ? (
            <div className="md:w-1/2">
              <NextActionCard nextAction={nextAction} />
            </div>
          ) : null}
        </div>

        <SectionCard title="History">
          <Timeline events={visibleEvents} />
        </SectionCard>
      </div>
    );
  };

  return (
    <SidebarLayout>
      <SidebarLayoutNavigation
        title="Unit Transfer Request"
        subTitle="Track the status of this transfer"
        secondaryTabs={getUnitTransferRequestTabs(requestId)}
        onBackButtonClick={() => navigate(backPath)}
      />
      <SidebarLayoutContent>{renderContent()}</SidebarLayoutContent>
    </SidebarLayout>
  );
};

export default UnitTransferRequestTracker;
